import { createContext, useEffect, useState, useCallback } from "react";
import supabase from "../Config/Supabase";
import db from "../local database/localDB";

const UsersContext = createContext();

export function UserContextProvider({ children }) {
  const [users, setUser] = useState([]);
  const [user, setUsers] = useState([]);
  const [farmers, setFarmers] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Sync when back online
      syncAllData();
    };

    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Load data on mount - offline first approach
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load data from local DB first, then sync if online
  const loadInitialData = async () => {
    try {
      // Load from local DB first for immediate rendering
      await Promise.all([
        loadLocalUsers(),
        loadLocalFarmers(),
        loadLocalUserInfo(),
      ]);

      // Then sync with server if online
      if (isOnline) {
        syncAllData();
      }
    } catch (error) {
      console.error("Error loading initial data:", error);
    }
  };

  // Load users from local DB
  const loadLocalUsers = async () => {
    try {
      const localUsers = await db.profiles.toArray();
      if (localUsers.length > 0) {
        setUser(localUsers);
      }
    } catch (error) {
      console.warn("Error loading users from local DB:", error);
    }
  };

  // Load farmers from local DB
  const loadLocalFarmers = async () => {
    try {
      const localFarmers = await db.farmers.toArray();
      if (localFarmers.length > 0) {
        setFarmers(localFarmers);
      }
    } catch (error) {
      console.warn("Error loading farmers from local DB:", error);
    }
  };

  // Load user info from local DB
  const loadLocalUserInfo = async () => {
    try {
      const localUserInfo = await db.userInfo.toArray();
      if (localUserInfo.length > 0) {
        setUsers(localUserInfo[0]); // Assuming single user info
      }
    } catch (error) {
      console.warn("Error loading user info from local DB:", error);
    }
  };

  // Sync all data
  const syncAllData = useCallback(async () => {
    if (!isOnline) return;

    await Promise.allSettled([fetchUsers(), fetchFarmers(), fetchUser()]);
  }, [isOnline]);

  // ✅ Gets list of users with offline-first logic
  const fetchUsers = async () => {
    try {
      // If offline, load from local DB
      if (!isOnline) {
        await loadLocalUsers();
        return;
      }

      const { data, error } = await supabase.from("user").select();
      if (error) throw new Error(error.message);

      setUser(data || []);

      // Save to local DB
      await db.profiles.clear();
      if (data && data.length > 0) {
        await db.profiles.bulkAdd(data);
      }
    } catch (error) {
      console.warn("Offline or error fetching users:", error.message);
      await loadLocalUsers();
    }
  };

  // ✅ Get the currently authenticated user with offline-first logic
  const fetchUser = async () => {
    try {
      // If offline, load from local DB
      if (!isOnline) {
        await loadLocalUserInfo();
        return;
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();

      setUsers(user);

      // Save to local DB
      await db.userInfo.clear();
      if (user) {
        await db.userInfo.add(user);
      }
    } catch (error) {
      console.warn(
        "Offline or error fetching authenticated user:",
        error.message,
      );
      await loadLocalUserInfo();
    }
  };

  // ✅ Gets list of farmers with offline-first logic
  const fetchFarmers = async () => {
    try {
      // If offline, load from local DB
      if (!isOnline) {
        await loadLocalFarmers();
        return;
      }

      let allFarmers = [];
      let start = 0;
      const batchSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error } = await supabase
          .from("farmer")
          .select("*")
          .order("created_at", { ascending: false })
          .range(start, start + batchSize - 1);

        if (error) throw new Error(error.message);

        if (Array.isArray(data) && data.length > 0) {
          allFarmers = [...allFarmers, ...data];
        }

        if (!data || data.length < batchSize) {
          hasMore = false;
        } else {
          start += batchSize;
        }
      }

      setFarmers(allFarmers);

      // Save to local DB
      await db.farmers.clear();
      if (allFarmers.length > 0) {
        await db.farmers.bulkAdd(allFarmers);
      }
    } catch (error) {
      console.warn("Offline or error fetching farmers:", error.message);
      await loadLocalFarmers();
    }
  };

  return (
    <UsersContext.Provider
      value={{
        users,
        farmers,
        user,
        isOnline,
        setFarmers,
        fetchFarmers,
        fetchUsers,
        fetchUser,
        syncAllData,
      }}
    >
      {children}
    </UsersContext.Provider>
  );
}

export default UsersContext;
